import { QueryClient } from '@tanstack/react-query';

import { AgentCounts, Conversation, Counts, DepartmentCounts } from '@/shared/types/chat';
import { ConversationAssignee } from '@/shared/types/rtm';

import { endpoints } from './useConversationsCounts';

/** The fields the count endpoints filter on. */
export type CountedState = ConversationAssignee & Pick<Conversation, 'desk_id' | 'created'>;

export type CountScope = {
  userId: string;
  canReadOthers: boolean;
  canRead: (state: CountedState) => boolean;
  isReady: boolean;
};

type Delta = {
  counts: Counts;
  agents: Record<string, number>;
  brain: number;
  departments: Record<string, number>;
};

// The count queries only include conversations created in the last 4 weeks.
const COUNT_WINDOW_MS = 28 * 24 * 60 * 60 * 1000;

const inWindow = (state: CountedState | null): state is CountedState =>
  !!state && Date.parse(state.created) >= Date.now() - COUNT_WINDOW_MS;

const bump = (record: Record<string, number>, key: string, sign: number) => {
  record[key] = (record[key] ?? 0) + sign;
};

const addState = (
  delta: Delta,
  state: CountedState | null,
  sign: number,
  scope: CountScope,
  isAccessLost = false
) => {
  if (!inWindow(state)) {
    return;
  }
  const isOpen = state.status === 'open';
  const readable = !isAccessLost && scope.canRead(state);
  const agentId = state.assignee_agent_id;

  if (readable) {
    const { counts } = delta;
    counts.inbox += isOpen && agentId === scope.userId ? sign : 0;
    counts.all += isOpen ? sign : 0;
    counts.unassigned += isOpen && !agentId && !state.assignee_brain_id ? sign : 0;
    counts.spam += state.status === 'spam' ? sign : 0;
    counts.trash += state.status === 'trash' ? sign : 0;
  }
  if (!isOpen) {
    return;
  }
  // Agent counts ignore departments; without read.others the server keeps only the user's own.
  if (agentId && (scope.canReadOthers || agentId === scope.userId)) {
    bump(delta.agents, agentId, sign);
  }
  if (readable && state.assignee_brain_id) {
    delta.brain += sign;
  }
  if (readable && state.department_id) {
    bump(delta.departments, state.department_id, sign);
  }
};

const add = (value: number, change = 0) => Math.max(0, value + change);

// The server leaves out a row with no open conversation, so a new one starts from 0.
const changeRows = <Row extends { open: number }>(
  rows: Row[],
  idOf: (row: Row) => string,
  changes: Record<string, number>,
  newRow: (id: string, open: number) => Row
) => {
  const known = new Set(rows.map(idOf));
  const added = Object.entries(changes)
    .filter(([id, change]) => change > 0 && !known.has(id))
    .map(([id, change]) => newRow(id, change));
  return [...rows.map((row) => ({ ...row, open: add(row.open, changes[idOf(row)]) })), ...added];
};

/**
 * Moves one conversation out of the counts of `before` and into the counts of `after`. The net
 * change is applied once, so an event that changes no counted field writes nothing. After a loss
 * of access, `after` still counts for its agent, because agent counts ignore read access.
 */
export const applyCountDelta = (params: {
  queryClient: QueryClient;
  deskId: string;
  before: CountedState | null;
  after: CountedState | null;
  isAccessLost?: boolean;
  scope: CountScope;
}) => {
  const { queryClient, deskId, before, after, isAccessLost, scope } = params;
  const delta: Delta = {
    counts: { inbox: 0, all: 0, unassigned: 0, spam: 0, trash: 0 },
    agents: {},
    brain: 0,
    departments: {},
  };
  addState(delta, before, -1, scope);
  addState(delta, after, 1, scope, isAccessLost);

  const { counts } = delta;
  if (Object.values(counts).some(Boolean)) {
    queryClient.setQueryData<Counts>(
      [endpoints.counts(deskId)],
      (prev) =>
        prev && {
          inbox: add(prev.inbox, counts.inbox),
          all: add(prev.all, counts.all),
          unassigned: add(prev.unassigned, counts.unassigned),
          spam: add(prev.spam, counts.spam),
          trash: add(prev.trash, counts.trash),
        }
    );
  }
  if (delta.brain || Object.values(delta.agents).some(Boolean)) {
    queryClient.setQueryData<AgentCounts>(
      [endpoints.agentCounts(deskId)],
      (prev) =>
        prev && {
          agents: changeRows(
            prev.agents,
            (a) => a.agent_id,
            delta.agents,
            (agent_id, open) => ({ agent_id, open })
          ),
          brain: { open: add(prev.brain.open, delta.brain) },
        }
    );
  }
  if (Object.values(delta.departments).some(Boolean)) {
    queryClient.setQueryData<DepartmentCounts>(
      [endpoints.departmentCounts(deskId)],
      (prev) =>
        prev && {
          departments: changeRows(
            prev.departments,
            (d) => d.department_id,
            delta.departments,
            (department_id, open) => ({ department_id, open })
          ),
        }
    );
  }
};
